/**
 * Автоматическое распознавание стен, дверей и окон со скана плана БТИ.
 *
 * После того как подложку положили и откалибровали (`mmPerPixel`), картинка уходит в фоновый
 * воркер, и стены ищет алгоритм «по заливке» (`fillRecognition.ts`): стены — сплошная
 * заливка, тонкие линии (текст, размеры, мебель, дуги дверей) стираются, куски стен
 * склеиваются через проёмы, концы сходятся в углах и стыках. Результат в пикселях
 * переводится в мм (`recognizedWalls.ts`), показывается превью, и выбранное строится
 * одним шагом истории (`commitRecognizedWalls`).
 */
import { WallMaterial } from "@/shared/catalog";
import { dist, projectOnSegment, type Pt } from "@/shared/lib/geometry";
import { OPENING_PRESET } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { useWallsStore } from "@/shared/store/layers";
import { addWallKeepingRooms } from "@/widgets/draw-wall/roomCoverings";
import type { FillWall } from "./fillRecognition";
import type { FillWorkerRequest, FillWorkerResponse } from "./fillRecognition.worker";
import { fillWallToRecognized, splitAtJunctions, type RecognizedWall } from "./recognizedWalls";

export interface RecognizeUnderlayOptions {
	/** Картинка подложки (data URL) */
	src: string;
	/** Левый верхний угол подложки на плане, мм */
	origin: Pt;
	/** Физический масштаб подложки, мм/пиксель */
	mmPerPixel: number;
	/** Чувствительность к тёмным пикселям (0..100, по умолчанию 50) */
	sensitivity: number;
	/** Минимальная толщина распознаваемой стены, мм */
	minThicknessMm?: number;
	/** Замыкать ли проёмы и автоматически ставить в них двери и окна */
	includeOpenings?: boolean;
}

/** Скан уменьшаем не сильнее этого: тонкую перегородку должно быть видно хотя бы в 3–4 пикселя. */
const MAX_PROCESS_SIDE = 2400;

/** Декодировать картинку подложки в ImageData, уменьшив до `MAX_PROCESS_SIDE` по большей стороне. */
const decodeUnderlayPixels = async (
	src: string,
): Promise<{ pixels: Uint8ClampedArray; width: number; height: number; scaleDown: number }> => {
	const response = await fetch(src);
	const blob = await response.blob();
	const bitmap = await createImageBitmap(blob);
	const scaleDown = Math.min(1, MAX_PROCESS_SIDE / Math.max(bitmap.width, bitmap.height));
	const width = Math.max(16, Math.round(bitmap.width * scaleDown));
	const height = Math.max(16, Math.round(bitmap.height * scaleDown));
	const actualScaleX = width / bitmap.width;
	const actualScaleY = height / bitmap.height;
	const effectiveScale = (actualScaleX + actualScaleY) * 0.5;

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		bitmap.close();
		throw new Error("Не удалось подготовить холст для распознавания.");
	}
	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, width, height);
	ctx.drawImage(bitmap, 0, 0, width, height);
	bitmap.close();
	const imageData = ctx.getImageData(0, 0, width, height);
	return { pixels: imageData.data, width, height, scaleDown: effectiveScale };
};

/** Распознать стены на подложке в фоновом модульном воркере. */
export const recognizeUnderlayWalls = async (
	options: RecognizeUnderlayOptions,
): Promise<RecognizedWall[]> => {
	const { pixels, width, height, scaleDown } = await decodeUnderlayPixels(options.src);
	const mmPerPixel = options.mmPerPixel / scaleDown;
	const walls = await new Promise<FillWall[]>((resolve, reject) => {
		const worker = new Worker(new URL("./fillRecognition.worker.ts", import.meta.url));
		worker.addEventListener("message", (event: MessageEvent<FillWorkerResponse>) => {
			worker.terminate();
			resolve(event.data.walls);
		});
		worker.addEventListener("error", (error) => {
			worker.terminate();
			reject(error);
		});
		const request: FillWorkerRequest = {
			pixels,
			width,
			height,
			mmPerPixel,
			sensitivity: options.sensitivity,
			minThicknessMm: options.minThicknessMm ?? 75,
		};
		worker.postMessage(request, [pixels.buffer]);
	});
	return walls
		.map((wall) =>
			fillWallToRecognized(wall, options.origin, mmPerPixel, options.includeOpenings ?? false),
		)
		.filter((wall) => dist(wall.from, wall.to) >= 150);
};

/**
 * Построить выбранные распознанные стены и врезать в них найденные двери и окна
 * одним шагом истории (`recordEdit`).
 */
export const commitRecognizedWalls = (walls: RecognizedWall[], height: number): number => {
	const active = walls.filter((w) => w.enabled);
	if (active.length === 0) return 0;
	let added = 0;
	useProjectStore.getState().recordEdit(() => {
		// 1. Сначала строим все стены. Перед этим режем их в точках пересечений и примыканий:
		// стена сквозь другую (крест) в модели не строится вовсе, а примыкание к середине уже
		// построенной стены без узла не замыкает комнату
		for (const piece of splitAtJunctions(active)) {
			// Несущие или нет — решает человек: распознанные стены пока все перегородки
			const endId = addWallKeepingRooms(piece.from, piece.to, {
				thickness: piece.thickness,
				height,
				material: WallMaterial.Block,
				bearing: false,
				snapTolerance: 90,
			});
			if (endId) added++;
		}

		// 2. Затем врезаем распознанные двери и окна в построенные куски стен
		const graph = useWallsStore.getState().items;
		const addOpening = useWallsStore.getState().addOpening;
		for (const wall of active) {
			if (!wall.openings || wall.openings.length === 0) continue;
			for (const opening of wall.openings) {
				let bestWallId: string | null = null;
				let bestDist = 180;
				let bestOffset = 0;
				let bestWallLen = 0;

				for (const candidate of Object.values(graph.walls)) {
					const a = graph.nodes[candidate.a];
					const b = graph.nodes[candidate.b];
					if (!a || !b) continue;
					const len = dist(a, b);
					if (len < 650) continue;
					const proj = projectOnSegment(opening.center, a, b);
					const offset = proj.t * len;
					if (proj.distance <= bestDist && offset >= 200 && offset <= len - 200) {
						bestDist = proj.distance;
						bestWallId = candidate.id;
						bestOffset = offset;
						bestWallLen = len;
					}
				}

				if (bestWallId) {
					const preset = OPENING_PRESET[opening.kind];
					const clampedWidth = Math.min(opening.width, Math.max(600, bestWallLen - 220));
					addOpening(bestWallId, opening.kind, bestOffset, {
						width: clampedWidth,
						height: preset.height,
						sill: preset.sill,
					});
				}
			}
		}
	});
	return added;
};
